import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './layout/Sidebar';
import Header from './layout/Header';
import AssistantChat from './assistant/AssistantChat';
import KnowledgeBasePanel from './assistant/KnowledgeBasePanel';
import HelpCenterPanel from './help/HelpCenterPanel';
import { WelcomeModal, TutorialOverlay } from './tutorial';
import AnimatedLogo from './common/AnimatedLogo';
import { ASSISTANT_NAME } from '../constants/branding';
import { useUIStore } from '../store';
import Tooltip from './common/Tooltip';
import { useHelpStore } from '../store/helpStore';
import { useTutorialStore } from '../store/tutorialStore';
import { useAssistantContext } from '../contexts/AssistantContext';
import { useProjectContext } from '../hooks/useProjectContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useWhatsNewStore } from '../store/whatsNewStore';
import WhatsNewModal from './whats-new/WhatsNewModal';
import { releases } from '../data/releaseNotes';

/**
 * AC-1652: Derive the current project id from a `/projects/:id/...` route.
 * Returns the numeric project id, or undefined when not on a project route.
 * Exported for reuse/testing.
 */
export function projectIdFromPath(pathname: string): number | undefined {
  const match = pathname.match(/^\/projects\/(\d+)/);
  return match ? Number(match[1]) : undefined;
}

// AC-1705: assistant-panel width bounds (px) and left-sidebar widths
// (Tailwind w-64 / w-16) used to cap the panel so it can stretch up to — but
// never under — the left menu.
const ASSISTANT_MIN_WIDTH = 320;
const ASSISTANT_DEFAULT_WIDTH = 384;
const SIDEBAR_EXPANDED_PX = 256;
const SIDEBAR_COLLAPSED_PX = 64;

const Layout: React.FC = () => {
  const {
    sidebarOpen,
    isMobileDrawerOpen,
    setMobileDrawerOpen,
    assistantOpen,
    closeAssistant,
    assistantWidth,
    setAssistantWidth,
  } = useUIStore();
  const { isHelpOpen, closeHelp } = useHelpStore();
  const { context } = useAssistantContext();
  const [isKnowledgePanelOpen, setIsKnowledgePanelOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  // AC-1652: the route project wins over a stale general AssistantContext, so
  // the assistant panel always carries the project the user is currently in.
  const routeProjectId = projectIdFromPath(location.pathname);
  const effProjectId = routeProjectId ?? context.projectId;
  const effContextType =
    context.contextType !== 'general'
      ? context.contextType
      : routeProjectId
        ? 'project'
        : 'general';
  // AC-1703: in the workflow editor, dock the copilot beside the canvas
  // (push content left) instead of overlaying it, so the user can watch the
  // graph update live while chatting. Everywhere else the panel overlays.
  const isWorkflowEditorRoute = /^\/projects\/\d+\/workflows\/\d+/.test(location.pathname);
  const dockAssistant = assistantOpen && isWorkflowEditorRoute && !isMobile;

  // AC-1705: drag-to-resize + maximize for the assistant panel. The chosen
  // width is persisted in the store; here we clamp it to the live viewport so
  // it can stretch up to (viewport - left sidebar) but no further.
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280,
  );
  const [isResizingAssistant, setIsResizingAssistant] = useState(false);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sidebarPx = isMobile ? 0 : sidebarOpen ? SIDEBAR_EXPANDED_PX : SIDEBAR_COLLAPSED_PX;
  const maxAssistantWidth = Math.max(ASSISTANT_MIN_WIDTH, viewportWidth - sidebarPx);
  const appliedAssistantWidth = Math.min(assistantWidth, maxAssistantWidth);
  const isAssistantMaximized = !isMobile && appliedAssistantWidth >= maxAssistantWidth - 1;

  const startAssistantResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingAssistant(true);
  }, []);

  useEffect(() => {
    if (!isResizingAssistant) return;
    const onMove = (e: MouseEvent) => {
      const sb = isMobile ? 0 : sidebarOpen ? SIDEBAR_EXPANDED_PX : SIDEBAR_COLLAPSED_PX;
      const max = Math.max(ASSISTANT_MIN_WIDTH, window.innerWidth - sb);
      const next = Math.min(Math.max(window.innerWidth - e.clientX, ASSISTANT_MIN_WIDTH), max);
      setAssistantWidth(next);
    };
    const onUp = () => setIsResizingAssistant(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizingAssistant, isMobile, sidebarOpen, setAssistantWidth]);

  const toggleAssistantMaximize = () =>
    setAssistantWidth(isAssistantMaximized ? ASSISTANT_DEFAULT_WIDTH : maxAssistantWidth);

  const { showPopup: showWhatsNew, checkForUpdates, dismissPopup: dismissWhatsNew } = useWhatsNewStore();

  // AC-471: Restore project context from URL on page reload
  useProjectContext();

  // AC-674: Tutorial state management
  const {
    status: tutorialStatus,
    showWelcomeModal,
    showCompletionModal,
    isOverlayHidden,
    loadTutorialState,
    startTutorial,
    dismissTutorial,
  } = useTutorialStore();

  // Load tutorial state on mount
  useEffect(() => {
    loadTutorialState();
  }, [loadTutorialState]);

  // Check for new releases on initial load only (not when tutorial status changes mid-session)
  const hasCheckedWhatsNew = useRef(false);
  useEffect(() => {
    if (hasCheckedWhatsNew.current) return;
    if ((tutorialStatus === 'completed' || tutorialStatus === 'dismissed') && !showWelcomeModal) {
      hasCheckedWhatsNew.current = true;
      checkForUpdates(releases[0].version);
    }
  }, [tutorialStatus, showWelcomeModal, checkForUpdates]);

  // Escape key handler to close mobile drawer
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileDrawerOpen, setMobileDrawerOpen]);

  // Close mobile drawer when assistant panel opens
  useEffect(() => {
    if (assistantOpen && isMobileDrawerOpen) {
      setMobileDrawerOpen(false);
    }
  }, [assistantOpen, isMobileDrawerOpen, setMobileDrawerOpen]);

  // AC-1699: the panel's open-state is persisted across reloads, but a
  // full-width takeover on a phone is jarring. Once, after the viewport size
  // is known, close a persisted-open panel on mobile so the user lands on
  // their content and opens the copilot deliberately.
  const didMobileAssistantGuard = useRef(false);
  useEffect(() => {
    if (didMobileAssistantGuard.current) return;
    if (isMobile && assistantOpen) {
      closeAssistant();
    }
    didMobileAssistantGuard.current = true;
  }, [isMobile, assistantOpen, closeAssistant]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-night">
      {/* ARIA live region for drawer state announcements */}
      <div aria-live="polite" className="sr-only">
        {isMobileDrawerOpen ? 'Navigation menu opened' : 'Navigation menu closed'}
      </div>

      {/* Mobile backdrop */}
      {isMobileDrawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileDrawerOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isMobileDrawerOpen={isMobileDrawerOpen}
        onMobileClose={() => setMobileDrawerOpen(false)}
      />

      {/* Main content area. AC-1703/AC-1705: when docked in the editor, the
          right margin follows the (resizable) panel width so the canvas reflows
          to match. Transition is suppressed mid-drag so the content tracks the
          handle without lag. */}
      <div
        className={`flex-1 flex flex-col overflow-hidden ${isResizingAssistant ? '' : 'transition-all duration-300'} ${
          isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
        }`}
        style={{ marginRight: dockAssistant ? appliedAssistantWidth : undefined }}
      >
        {/* Header */}
        <Header
          onMenuToggle={() => setMobileDrawerOpen(true)}
        />

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Assistant Panel - slides in from right like sidebar. AC-1705: width is
          user-resizable (drag handle) and maximizable, persisted in the store
          and clamped to the viewport. */}
      <div
        className={`fixed inset-y-0 right-0 z-40 bg-white dark:bg-cap-dark border-l border-gray-200 dark:border-gray-700 overflow-hidden ${isResizingAssistant ? '' : 'transition-all duration-300'}`}
        style={{ width: assistantOpen ? (isMobile ? '100%' : appliedAssistantWidth) : 0 }}
      >
        {/* AC-1705: drag the left edge to resize the panel. */}
        {assistantOpen && !isMobile && (
          <div
            onMouseDown={startAssistantResize}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize assistant panel"
            className="absolute inset-y-0 left-0 w-1.5 cursor-col-resize hover:bg-primary-400/40 active:bg-primary-500/50 z-10 transition-colors"
          />
        )}
        <div className="flex flex-col h-full">
          {/* Assistant Header — branded as the Agent Commander copilot (AC-1700).
              The animated mascot salutes when a reply arrives; the scope subtitle
              (General / Project / Workflow) tells the user what the copilot is
              acting on and distinguishes it from the app chrome of the same name. */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <AnimatedLogo size={32} className="flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-warm-white">{ASSISTANT_NAME}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {effContextType === 'orchestration' ? 'Workflow' :
                   effContextType === 'project' ? 'Project' : 'General'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Tooltip content="Search Knowledge Base">
              <button
                onClick={() => setIsKnowledgePanelOpen(true)}
                className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              </Tooltip>
              {!isMobile && (
                <Tooltip content={isAssistantMaximized ? 'Restore panel width' : 'Maximize panel'}>
                <button
                  onClick={toggleAssistantMaximize}
                  aria-label={isAssistantMaximized ? 'Restore assistant panel width' : 'Maximize assistant panel'}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                >
                  {isAssistantMaximized ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <polyline points="4 14 10 14 10 20" />
                      <polyline points="20 10 14 10 14 4" />
                      <line x1="14" y1="10" x2="21" y2="3" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  )}
                </button>
                </Tooltip>
              )}
              <Tooltip content="Close Assistant">
              <button
                onClick={closeAssistant}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </Tooltip>
            </div>
          </div>

          {/* Assistant Chat Content */}
          <div className="flex-1 overflow-hidden">
            <AssistantChat
              projectId={effProjectId}
              contextType={effContextType}
              contextId={context.contextId}
              onClose={closeAssistant}
            />
          </div>
        </div>
      </div>

      {/* Knowledge Base Panel */}
      {isKnowledgePanelOpen && (
        <KnowledgeBasePanel
          isOpen={isKnowledgePanelOpen}
          onClose={() => setIsKnowledgePanelOpen(false)}
          onSelectKnowledge={(knowledge) => {
            // Handle knowledge selection - this will be passed to AssistantChat
            console.log('Knowledge selected:', knowledge);
            setIsKnowledgePanelOpen(false);
          }}
          projectId={undefined}
        />
      )}

      {/* Help Center Panel - AC-659 */}
      <HelpCenterPanel isOpen={isHelpOpen} onClose={closeHelp} />

      {/* Tutorial System - AC-674 */}
      {/* Welcome Modal - shown to new users who haven't started the tutorial */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onStart={startTutorial}
        onSkip={dismissTutorial}
      />

      {/* What's New Modal - shown to returning users when a new release is available */}
      <WhatsNewModal
        isOpen={showWhatsNew && !showWelcomeModal && !showCompletionModal}
        release={releases[0]}
        onDismiss={() => dismissWhatsNew(releases[0].version)}
        onViewAll={() => { dismissWhatsNew(releases[0].version); navigate('/whats-new'); }}
      />

      {/* Tutorial Overlay - shown when tutorial is in progress (and not paused) or completion modal is visible */}
      {((tutorialStatus === 'in_progress' && !isOverlayHidden) || showCompletionModal) && <TutorialOverlay />}
    </div>
  );
};

export default Layout;
